/**
 * TODO: [HALO-MIGRATION] Delete this entire file after migration is complete
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Feedback } from '@fashionnovateam/nova-ui-toolkit';

const HALO_CREATOR_CENTER_URL = 'https://creator.fashionnova.com';
const MODAL_DISMISSED_KEY = 'halo_migration_modal_dismissed';

type MigrationType = 'influencer' | 'agency';

interface HaloMigrationModalProps {
  migrationType: MigrationType;
}

const CONTENT = {
  influencer: {
    title: 'YOUR NEW CREATOR CENTER HAS ARRIVED.',
    subtitle:
      'Experience a more intuitive way to manage your partnership. Your upgraded home for growth is here.',
    image: '/assets/migration/creator_modal.png',
    mobileImage: '/assets/migration/creator_modal_mobile.png',
    imageAlt: 'Creator Center Preview',
  },
  agency: {
    title: 'YOUR NEW AGENCY PORTAL HAS ARRIVED.',
    subtitle:
      'Experience a more intuitive way to manage your influencers and agents. Your upgraded home for growth is here.',
    image: '/assets/migration/agency_modal.png',
    mobileImage: '/assets/migration/agency_modal_mobile.png',
    imageAlt: 'Agency Portal Preview',
  },
};

function HaloMigrationModal({ migrationType }: HaloMigrationModalProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(MODAL_DISMISSED_KEY);
    if (!dismissed) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.setItem(MODAL_DISMISSED_KEY, 'true');
    setOpen(false);
  };

  const handleGoToAccount = () => {
    sessionStorage.setItem(MODAL_DISMISSED_KEY, 'true');
    window.open(HALO_CREATOR_CENTER_URL, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const content = CONTENT[migrationType];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '850px',
          maxHeight: '675px',
          width: '100%',
          height: '100%',
          overflow: 'visible',
        },
      }}
    >
      {/* Top bar with close button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: '12px',
          pr: '20px',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: 'black',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'visible',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: { md: '480px' },
          height: '100%',
          position: 'relative',
        }}
      >
        {/* Right side - Image (desktop) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'flex-end',
            position: 'absolute',
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            top: -5,
          }}
        >
          <Box
            component="img"
            src={content.image}
            alt={content.imageAlt}
            sx={{
              objectFit: 'contain',
              height: '100%',
            }}
          />
        </Box>

        {/* Left side - Content */}
        <Box
          sx={{
            paddingTop: '30px',
            paddingLeft: '48px',
            display: 'flex',
            flexDirection: 'column',
            width: '390px',
            height: '100%',
            position: 'relative',
          }}
        >
          {/* Header Logo */}
          <Box
            component="img"
            src="/assets/migration/logo-light.png"
            alt="FashionNova Creators"
            sx={{
              width: '199.83px',
              mb: '30px',
            }}
          />

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontStyle: 'bold',
              size: '32px',
              lineHeight: '32px',
              color: 'black',
              mb: '10px',
            }}
          >
            {content.title}
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              fontSize: '20px',
              color: 'black',
              mb: '30px',
              lineHeight: '24px',
            }}
          >
            {content.subtitle}
          </Typography>

          {/* Checklist */}
          <Box sx={{ mb: '30px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckIcon sx={{ fontSize: 18, color: 'black' }} />
              <Typography sx={{ fontSize: '13px', color: 'black' }}>
                Sign in with the same email as this account
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: 18, color: 'black' }} />
              <Typography sx={{ fontSize: '13px', color: 'black' }}>
                Continue to view all of your payment details
              </Typography>
            </Box>
          </Box>

          {/* Info box */}
          <Feedback
            className="p-[12px] mb-[30px]"
            type="info"
            title="Any edits to your profile or payment details must be made in the
              new portal."
          />

          {/* CTA Button */}
          <Button
            variant="contained"
            onClick={handleGoToAccount}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '20px',
              px: 3,
              py: 1.5,
              borderRadius: '90px',
              alignSelf: 'flex-start',
            }}
          >
            Go to your account
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default HaloMigrationModal;
